/**
 * Reports page — сводка по воронке.
 * GET /api/reports?client_id=<activeClientId>
 *
 * КАНОН: имена полей = backend ReportsResponse (reports_router.py):
 *   total_deals, won_deals, lost_deals, conversion_rate (0–100, считает бэк),
 *   avg_deal_value, by_stage: [{stage_key, stage_name, count, value_total}].
 */

import { BarChart3 } from 'lucide-react'
import { useReports } from '@/hooks/useBilling'
import { useActiveClient } from '@/hooks/useClients'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatRub } from '@/lib/utils'

export function ReportsPage() {
  const { activeClientId } = useActiveClient()
  const { data: reports, isLoading, error } = useReports(activeClientId)

  if (isLoading) return <div className="p-4 text-muted-foreground">Загрузка отчётов...</div>
  if (error || !reports) return <div className="p-4 text-destructive">Ошибка загрузки отчётов</div>

  const byStage = reports.by_stage ?? []
  const maxCount = Math.max(...byStage.map((r) => r.count), 1)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Отчёты</h1>
        <p className="text-sm text-muted-foreground">
          Воронка{activeClientId ? '' : ' · все клиенты'}
        </p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Всего сделок</p>
            <p className="text-2xl font-bold mt-1">{reports.total_deals}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">На стадии «Договор»</p>
            <p className="text-2xl font-bold mt-1 text-green-600">{reports.won_deals}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Проиграно</p>
            <p className="text-2xl font-bold mt-1 text-destructive">{reports.lost_deals}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Конверсия в договор</p>
            <p className="text-2xl font-bold mt-1">{Math.round(reports.conversion_rate)}%</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Средний чек</p>
            <p className="text-2xl font-bold mt-1">
              {reports.avg_deal_value > 0 ? formatRub(reports.avg_deal_value) : '—'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* By stage table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">По стадиям воронки</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {/* Header */}
            <div className="flex items-center gap-4 text-xs font-medium text-muted-foreground pb-1 border-b">
              <span className="flex-1">Стадия</span>
              <span className="w-16 text-right">Кол-во</span>
              <span className="w-24 text-right">Сумма</span>
              <span className="w-32">Доля</span>
            </div>

            {byStage.length === 0 && (
              <p className="py-4 text-center text-sm text-muted-foreground">
                Нет активных сделок
              </p>
            )}

            {byStage.map((row) => {
              const barPct = Math.round((row.count / maxCount) * 100)
              return (
                <div key={row.stage_key} className="flex items-center gap-4 text-sm">
                  <span className="flex-1 truncate">{row.stage_name || row.stage_key}</span>
                  <span className="w-16 text-right font-medium">{row.count}</span>
                  <span className="w-24 text-right text-muted-foreground tabular-nums">
                    {row.value_total > 0 ? formatRub(row.value_total) : '—'}
                  </span>
                  <div className="w-32">
                    <div className="h-2 w-full rounded-full bg-secondary overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary transition-all"
                        style={{ width: `${barPct}%` }}
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
